import { ConversationParticipantEntity } from '@provision/conversation-types';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { DynamicIcon } from 'src/components';
import { PreparedConversationParticipantOption } from 'src/graphql';
import { useConversationParticipation } from 'src/graphql/entities/conversation-participation/conversation-participation';
import { useConversationEntity } from 'src/pages/Conversations/SidePanel/ConversationDetails/properties/conversation-entity-context';
import { Modals, useShowModals } from 'src/store/modals';
import { AddParticipantMenu } from './add-participant-menu';
import { ClientHoverCardContent } from './client-hover-card-content';
import { toParticipantEntity } from './conversation-enum-values';
import { ParticipantPillMenuItem } from './participant-pill';
import { buildNewClientPreseed } from './participant-preseed';
import {
  ParticipantPillItem,
  UseConversationParticipantPillsResult,
} from './use-conversation-participant-pills';

const FOLLOWER_HELP_TEXT =
  'Followers will receive email notifications about new messages in this conversation. Agent email addresses will not be exposed. Replies to these notifications will be treated as public replies.';

const PARTICIPANT_ID_KEY_PREFIX = 'id:';
const PARTICIPANT_IDENTITY_KEY_PREFIX = 'identity:';
const PENDING_KEY_PREFIX = 'pending:';

/** An "+ Add" pick that's been fired off but hasn't shown up in `pills` yet. */
export interface PendingParticipantPill {
  key: string;
  entity: ConversationParticipantEntity;
  displayName: string;
}

interface PendingParticipantAdd {
  key: string;
  option: PreparedConversationParticipantOption;
}

export interface UseParticipantPillActionsParams {
  pills: ParticipantPillItem[];
  refetchParticipations: UseConversationParticipantPillsResult['refetchParticipations'];
}

export interface UseParticipantPillActionsResult {
  getMenuItems: (item: ParticipantPillItem) => ParticipantPillMenuItem[] | undefined;
  getMenuHelpText: (item: ParticipantPillItem) => string | undefined;
  getHoverCard: (item: ParticipantPillItem) => ReactNode | undefined;
  renderAddControl: () => ReactNode;
  pendingParticipants: PendingParticipantPill[];
}

const isActiveAgent = (item: ParticipantPillItem) =>
  item.entity === ConversationParticipantEntity.PlatformUser && !item.isDeactivatedAgent;

// Same identity fields `add-participant-menu.tsx` uses to filter options that
// already have a pill — reused here so a pending pill disappears exactly when
// the option it was built from would now be filtered out of the dropdown.
const getPillIdentityKeys = (item: ParticipantPillItem): string[] => {
  const keys: string[] = [];

  if (item.conversationParticipantId) {
    keys.push(`${PARTICIPANT_ID_KEY_PREFIX}${item.conversationParticipantId}`);
  }
  if (item.participantIdentity) {
    keys.push(`${PARTICIPANT_IDENTITY_KEY_PREFIX}${item.participantIdentity.toLowerCase()}`);
  }
  if (item.clientPrimaryEmail) {
    keys.push(`${PARTICIPANT_IDENTITY_KEY_PREFIX}${item.clientPrimaryEmail.toLowerCase()}`);
  }

  return keys;
};

const getOptionIdentityKeys = (option: PreparedConversationParticipantOption): string[] => {
  const keys: string[] = [];

  if (option.conversationParticipantId) {
    keys.push(`${PARTICIPANT_ID_KEY_PREFIX}${option.conversationParticipantId}`);
  }
  if (option.value) {
    keys.push(`${PARTICIPANT_IDENTITY_KEY_PREFIX}${option.value.toLowerCase()}`);
  }
  if (option.email) {
    keys.push(`${PARTICIPANT_IDENTITY_KEY_PREFIX}${option.email.toLowerCase()}`);
  }

  return keys;
};

const getPendingKey = (option: PreparedConversationParticipantOption) =>
  `${PENDING_KEY_PREFIX}${option.id}`;

export const useParticipantPillActions = ({
  pills,
  refetchParticipations,
}: UseParticipantPillActionsParams): UseParticipantPillActionsResult => {
  const { conversation } = useConversationEntity();
  const { addParticipationWithChannel, deactivateParticipation } =
    useConversationParticipation();
  const [showAssociateParticipantWithClientModal, showAddClientModal] = useShowModals(
    Modals.AssociateParticipantWithClientModal,
    Modals.AddClientModal,
  );

  const [pendingAdds, setPendingAdds] = useState<PendingParticipantAdd[]>([]);

  const addPendingParticipant = useCallback(
    (option: PreparedConversationParticipantOption) => {
      const key = getPendingKey(option);
      setPendingAdds((prev) =>
        prev.some((pending) => pending.key === key) ? prev : [...prev, { key, option }],
      );
      return key;
    },
    [],
  );

  // Only used for rollback on failure — a successful add is superseded once the
  // refetched participation records produce the matching real pill (see
  // `pendingParticipants` below), so there's no separate "success" clearing path.
  const removePendingParticipant = useCallback((key: string) => {
    setPendingAdds((prev) => prev.filter((pending) => pending.key !== key));
  }, []);

  const pendingParticipants = useMemo<PendingParticipantPill[]>(() => {
    const activeIdentityKeys = new Set(pills.flatMap(getPillIdentityKeys));

    return pendingAdds
      .filter(
        (pending) =>
          !getOptionIdentityKeys(pending.option).some((key) => activeIdentityKeys.has(key)),
      )
      .map((pending) => ({
        key: pending.key,
        entity: toParticipantEntity(pending.option.participantEntity),
        displayName: pending.option.label,
      }));
  }, [pendingAdds, pills]);

  const toggleFollow = useCallback(
    async (item: ParticipantPillItem) => {
      if (item.isFollower && item.rawEmailParticipation) {
        await deactivateParticipation(item.rawEmailParticipation.channel, conversation);
      } else {
        await addParticipationWithChannel({
          participantIdentity: item.participantIdentity,
          conversation,
          email: item.platformUserEmail ?? '',
          participantEntity: ConversationParticipantEntity.PlatformUser,
        });
      }
      void refetchParticipations();
    },
    [
      conversation,
      deactivateParticipation,
      addParticipationWithChannel,
      refetchParticipations,
    ],
  );

  const getMenuItems = useCallback(
    (item: ParticipantPillItem): ParticipantPillMenuItem[] | undefined => {
      if (isActiveAgent(item)) {
        return [
          {
            icon: (
              <DynamicIcon
                name={item.isFollower ? 'bell-off' : 'bell'}
                library="lucide"
                size={14}
              />
            ),
            label: item.isFollower ? 'Remove as follower' : 'Set to follower',
            onClick: () => void toggleFollow(item),
          },
        ];
      }

      if (item.isUnidentified) {
        return [
          {
            icon: <DynamicIcon name="user-search" library="lucide" size={14} />,
            label: 'Merge into existing client…',
            testId: 'participant-pill-merge-into-client',
            onClick: () =>
              showAssociateParticipantWithClientModal({
                participant: item.participant,
                partnerId: item.participantPartnerId ?? conversation.partnerId,
              }),
          },
          {
            icon: <DynamicIcon name="user-plus" library="lucide" size={14} />,
            label: 'Add as new client',
            onClick: () =>
              showAddClientModal({
                client: buildNewClientPreseed(item.participantIdentity),
                participant: item.participant,
                conversation,
              }),
          },
        ];
      }

      return undefined;
    },
    [toggleFollow, showAssociateParticipantWithClientModal, showAddClientModal, conversation],
  );

  const getMenuHelpText = useCallback(
    (item: ParticipantPillItem): string | undefined =>
      isActiveAgent(item) ? FOLLOWER_HELP_TEXT : undefined,
    [],
  );

  const getHoverCard = useCallback((item: ParticipantPillItem): ReactNode | undefined => {
    if (item.entity !== ConversationParticipantEntity.Client || item.isDeletedClient) {
      return undefined;
    }

    return <ClientHoverCardContent item={item} />;
  }, []);

  const renderAddControl = useCallback(
    () => (
      <AddParticipantMenu
        conversation={conversation}
        pills={pills}
        onAdded={() => void refetchParticipations()}
        onAddStart={addPendingParticipant}
        onAddSettled={(key, success) => {
          if (!success) {
            removePendingParticipant(key);
          }
        }}
      />
    ),
    [
      conversation,
      pills,
      refetchParticipations,
      addPendingParticipant,
      removePendingParticipant,
    ],
  );

  return {
    getMenuItems,
    getMenuHelpText,
    getHoverCard,
    renderAddControl,
    pendingParticipants,
  };
};
